import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

/**
 * Iter-311 — Fetch client-scoped business rules.
 *
 * Behaviour:
 *   - When `clientId` is null/undefined → hits `/api/client-rules/me` (for the
 *     logged-in client's own shift form).
 *   - When `clientId` is set → hits `/api/client-rules/for-client/{id}`
 *     (admin creating a booking on behalf of a client).
 *
 * Returned shape:
 *   {
 *     loading: boolean,
 *     lockedBillRate: number | null,   // e.g. 38.00 for SweetWater
 *     discoveryRadiusMiles: number,    // default 75
 *     blockedRoleIds: Set<string>,     // roles this client cannot book
 *     roleReplacements: Map<string, string>,  // sourceRoleId → substituteRoleId
 *     rulesRaw: array,                 // full rule rows for debugging
 *     refetch: () => Promise<void>,
 *   }
 */
export default function useClientBusinessRules(clientId) {
  const [state, setState] = useState({
    loading: true,
    lockedBillRate: null,
    discoveryRadiusMiles: 75,
    blockedRoleIds: new Set(),
    roleReplacements: new Map(),
    rulesRaw: [],
  });

  const fetchRules = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      if (!token) {
        setState({
          loading: false,
          lockedBillRate: null,
          discoveryRadiusMiles: 75,
          blockedRoleIds: new Set(),
          roleReplacements: new Map(),
          rulesRaw: [],
        });
        return;
      }
      const url = clientId
        ? `${API}/api/client-rules/for-client/${clientId}`
        : `${API}/api/client-rules/me`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data || {};
      const blocked = new Set();
      const remaps = new Map();
      (data.rules || []).forEach((r) => {
        if (r.rule_type === 'role_block' && r.role_id) blocked.add(r.role_id);
        if (r.rule_type === 'role_remap' && r.role_id && r.target_role_id) {
          remaps.set(r.role_id, r.target_role_id);
        }
      });
      setState({
        loading: false,
        lockedBillRate: data.locked_bill_rate ?? null,
        discoveryRadiusMiles: data.discovery_radius_miles ?? 75,
        blockedRoleIds: blocked,
        roleReplacements: remaps,
        rulesRaw: data.rules || [],
      });
    } catch (e) {
      // Silent degrade — absence of rules is a valid state.
      setState({
        loading: false,
        lockedBillRate: null,
        discoveryRadiusMiles: 75,
        blockedRoleIds: new Set(),
        roleReplacements: new Map(),
        rulesRaw: [],
      });
    }
  }, [clientId]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  return { ...state, refetch: fetchRules };
}
